import { defineStore } from 'pinia';
import * as WidgetConfig from '@/types/WidgetConfig';
import { useConnectionStore } from '@/stores/connection';
import type { Mutation, Query, WidgetAttributes } from '@/generated/graphql';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

type WidgetsStore = {
  widgets: WidgetConfig.WidgetConfig[];
  widgetsLoading: boolean;
};

const WIDGET_FRAGMENT = gql`
  fragment WidgetFields on WidgetConfig {
    id
    description
    loaderUrl
    style
    token
    embedUrl
    configuration
    style
  }
`;

const WIDGETS_QUERY = gql`
  query fetchWidgets($accountId: Int!) {
    app {
      widgetConfigurations(accountId: $accountId) {
        collection {
          ...WidgetFields
        }
      }
    }
  }
  ${WIDGET_FRAGMENT}
`;

const SAVE_WIDGET_MUTATION = gql`
  mutation saveWidgetMutation($widgetAttributes: WidgetAttributes!) {
    app {
      saveWidget(input: { widget: $widgetAttributes }) {
        widget {
          ...WidgetFields
        }
      }
    }
  }
  ${WIDGET_FRAGMENT}
`;

const DELETE_WIDGET_MUTATION = gql`
  mutation deleteWidgetMutation($id: Int!) {
    app {
      deleteWidget(input: { id: $id }) {
        errors
      }
    }
  }
`;

export const useWidgetsStore = defineStore('widgets-store', {
  state: (): WidgetsStore => ({
    widgets: [],
    widgetsLoading: false,
  }),

  actions: {
    async fetchWidgets(accountId: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      this.widgetsLoading = true;
      try {
        const { data } = await apiClient.query<Query>({
          query: WIDGETS_QUERY,
          variables: { accountId },
          fetchPolicy: 'network-only',
        });
        this.widgets = data.app!.widgetConfigurations.collection.map(WidgetConfig.fromGql);
      } finally {
        this.widgetsLoading = false;
      }
    },

    async saveWidget(accountId: number, payload: WidgetAttributes) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      this.widgetsLoading = true;
      try {
        await apiClient.mutate<Mutation>({
          mutation: SAVE_WIDGET_MUTATION,
          variables: {
            widgetAttributes: payload,
          },
        });
      } finally {
        this.widgetsLoading = false;
      }
    },

    async deleteWidget(accountId: number, widgetId: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      this.widgetsLoading = true;
      try {
        await apiClient.mutate<Mutation>({
          mutation: DELETE_WIDGET_MUTATION,
          variables: {
            id: widgetId,
          },
        });
      } finally {
        this.widgetsLoading = false;
      }
    },
  },
});
